import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import CountryDeleteDialog from "@saleor/countries/components/CountryDeleteDialog";
import CountryPage, {
  CountryPageFormData
} from "@saleor/countries/components/CountryPage";
import RegionDeleteDialog from "@saleor/countries/components/RegionDeleteDialog";
import RegionEditDialog from "@saleor/countries/components/RegionEditDialog";
import {
  useCountryDeleteMutation,
  useCountryUpdateMutation,
  useRegionCreateMutation,
  useRegionDeleteMutation,
  useRegionUpdateMutation
} from "@saleor/countries/mutations";
import { useCountryDetailsQuery } from "@saleor/countries/queries";
import {
  countryListUrl,
  countryUrl,
  CountryUrlDialog,
  CountryUrlQueryParams
} from "@saleor/countries/urls";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import useCurrencySearch from "@saleor/searches/useCurrencySearch";
import useLanguageSearch from "@saleor/searches/useLanguageSearch";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React from "react";
import { useIntl } from "react-intl";

interface CountryDetailsProps {
  id: string;
  params: CountryUrlQueryParams;
}

const CountryDetails: React.FC<CountryDetailsProps> = ({ id, params }) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();

  const [openModal, closeModal] = createDialogActionHandlers<
    CountryUrlDialog,
    CountryUrlQueryParams
  >(navigate, params => countryUrl(id, params), params);

  const { data, loading } = useCountryDetailsQuery({
    variables: {
      id
    }
  });
  const [countryDelete, countryDeleteOpts] = useCountryDeleteMutation({
    onCompleted: data => {
      if (data.countryDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Country deleted"
          })
        });
        navigate(countryListUrl());
      }
    }
  });

  const [regionDelete, regionDeleteOpts] = useRegionDeleteMutation({
    onCompleted: data => {
      if (data.regionDelete.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "region deleted",
            description: "region  deleted"
          })
        });
        closeModal();
      }
    }
  });

  const [countryUpdate, countryUpdateOpts] = useCountryUpdateMutation({
    onCompleted: data => {
      if (data.countryUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
      }
    }
  });

  const [regionUpdate, regionUpdateOpts] = useRegionUpdateMutation({
    onCompleted: data => {
      if (data.regionUpdate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        closeModal();
      }
    }
  });

  const [regionCreate, regionCreateOpts] = useRegionCreateMutation({
    onCompleted: data => {
      if (data.regionCreate.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage({
            defaultMessage: "Added new region",
            description: "added new region"
          })
        });
        closeModal();
      }
    }
  });

  const handleUpdate = async (data: CountryPageFormData) => {
    const input = {
      ...data,
      defaultCurrency: data.defaultCurrency ? data.defaultCurrency.code : "",
      defaultLanguage: data.defaultLanguage
        ? data.defaultLanguage.languageCode
        : ""
    };
    const result = await countryUpdate({
      variables: {
        id,
        input
      }
    });

    return result.data.countryUpdate.errors;
  };

  const handleSubmit = handleUpdate;

  const [, setTextChange] = React.useState<boolean>();
  const {
    loadMore: loadMoreLanguages,
    search: searchLanguage,
    result: searchLanguageOpts
  } = useLanguageSearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  const {
    loadMore: loadMoreCurrencyrates,
    search: searchCurrencyrate,
    result: searchCurrencyrateOpts
  } = useCurrencySearch({
    variables: DEFAULT_INITIAL_SEARCH_DATA
  });

  return (
    <>
      <CountryPage
        country={maybe(() => data.country)}
        disabled={loading}
        errors={countryUpdateOpts.data?.countryUpdate.errors || []}
        onBack={() => navigate(countryListUrl())}
        onDelete={() => openModal("remove")}
        onSubmit={handleSubmit}
        onValueAdd={() => openModal("add-value")}
        onValueDelete={id =>
          openModal("remove-value", {
            id
          })
        }
        onValueReorder={null}
        onValueUpdate={id =>
          openModal("edit-value", {
            id
          })
        }
        languages={
          searchLanguageOpts.data?.search?.edges.map(edge => edge.node) || []
        }
        fetchLanguages={searchLanguage}
        fetchMoreLanguages={{
          hasMore: searchLanguageOpts.data?.search?.pageInfo.hasNextPage,
          loading: searchLanguageOpts.loading,
          onFetchMore: loadMoreLanguages
        }}
        currencies={
          searchCurrencyrateOpts.data?.search?.edges.map(edge => edge.node) ||
          []
        }
        fetchCurrencyrates={searchCurrencyrate}
        fetchMoreCurrencyrates={{
          hasMore: searchCurrencyrateOpts.data?.search?.pageInfo.hasNextPage,
          loading: searchCurrencyrateOpts.loading,
          onFetchMore: loadMoreCurrencyrates
        }}
        saveButtonBarState={countryUpdateOpts.status}
        regions={maybe(() => data.country.regions)}
      />
      <CountryDeleteDialog
        open={params.action === "remove"}
        name={maybe(() => data.country.name, "...")}
        confirmButtonState={countryDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          countryDelete({
            variables: {
              id
            }
          })
        }
      />
      <RegionDeleteDialog
        open={params.action === "remove-value"}
        name={maybe(
          () => data.country.regions.find(item => params.id === item.id).name,
          "..."
        )}
        useName={true}
        confirmButtonState={regionDeleteOpts.status}
        onClose={closeModal}
        onConfirm={() =>
          regionDelete({
            variables: {
              id: params.id
            }
          })
        }
      />
      <RegionEditDialog
        regions={null}
        confirmButtonState={regionCreateOpts.status}
        disabled={loading}
        errors={regionCreateOpts.data?.regionCreate.errors || []}
        open={params.action === "add-value"}
        onClose={closeModal}
        onSubmit={async inputdata => {
          const input = {
            country: data.country.id,
            name: inputdata.name,
            code: inputdata.code,
            enable: inputdata.reenable,
            displayOrder: inputdata.displayOrder
          };
          const result = await regionCreate({
            variables: {
              input
            }
          });
          return result.data.regionCreate.errors;
        }}
        textChange={setTextChange}
      />
      <RegionEditDialog
        regions={maybe(() =>
          data.country.regions.find(value => params.id === value.id)
        )}
        confirmButtonState={regionUpdateOpts.status}
        disabled={loading}
        errors={regionUpdateOpts.data?.regionUpdate.errors || []}
        open={params.action === "edit-value"}
        onClose={closeModal}
        onSubmit={inputdata => {
          const input = {
            country: data.country.id,
            name: inputdata.name,
            code: inputdata.code,
            enable: inputdata.reenable,
            displayOrder: inputdata.displayOrder
          };
          return regionUpdate({
            variables: {
              id: data.country.regions.find(item => params.id === item.id).id,
              input
            }
          });
        }}
        textChange={setTextChange}
      />
    </>
  );
};

export default CountryDetails;
